import { Suspense } from 'react';
import { Await, Navigate, useLoaderData } from 'react-router-dom';
import { Box, CircularProgress, Typography } from '@mui/material';
import { Formik, useFormikContext } from 'formik';

import { FEATURE_FLAGS } from '~common';
import {
  ErrorMessage,
  FeatureFlagsRenderer,
  ForgotPassword,
} from '~components/atoms';
import { LoadingButton, PaddingBox } from '~components/atoms/custom';
import { TextField } from '~components/atoms/form';
import {
  LoginProvider,
  loginValidationSchema,
  LoginValues,
  useLoginContext,
} from '~providers/LoginProvider';

export const LoginPage = () => {
  const { userPromise } = useLoaderData() as {
    userPromise: Promise<unknown>;
  };

  return (
    <Suspense
      fallback={
        <Box
          display="flex"
          width="100vw"
          height="100vh"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress />
        </Box>
      }
    >
      <Await
        resolve={userPromise}
        errorElement={
          <LoginProvider>
            <LoginPageViewWrapper />
          </LoginProvider>
        }
        children={<Navigate to={'/'} replace />}
      />
    </Suspense>
  );
};

export const LoginPageViewWrapper = () => {
  const { loginValues, handleSubmit } = useLoginContext();

  return (
    <Formik
      initialValues={loginValues}
      onSubmit={handleSubmit}
      enableReinitialize
      validationSchema={loginValidationSchema}
    >
      <LoginPageView />
    </Formik>
  );
};

export const LoginPageView = () => {
  const { isLoggingIn } = useLoginContext();

  const { submitForm, errors, dirty } = useFormikContext<LoginValues>();

  return (
    <PaddingBox
      display="flex"
      flexDirection="column"
      alignItems="center"
      gap={3}
      width="100%"
    >
      {/* Form Input */}
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        gap={2}
        width="100%"
      >
        {/* Error Display */}
        {errors.submit && <ErrorMessage errorText={errors.submit} />}

        <Typography variant="h6" fontWeight={600}>
          Login
        </Typography>
        <TextField
          label={
            <Typography variant="body2" fontWeight={400}>
              Email
            </Typography>
          }
          name="email"
          type="email"
        />
        <TextField
          label={
            <Typography variant="body2" fontWeight={400}>
              Password
            </Typography>
          }
          name="password"
          type="password"
          autoComplete="on"
        />
      </Box>

      {/* Login Button */}

      <LoadingButton
        onClick={submitForm}
        disabled={!dirty}
        isLoading={isLoggingIn}
      >
        <Typography variant="body2" fontWeight={500}>
          Login
        </Typography>
      </LoadingButton>

      {/* Forgot Password */}
      <FeatureFlagsRenderer
        ffKey={FEATURE_FLAGS.WEB_DASHBOARD_RESET_PASSWORD}
        component={ForgotPassword}
      />
    </PaddingBox>
  );
};
